import {
  AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef,
  EventEmitter, HostListener, Input, NgZone, OnChanges, OnDestroy, Output,
  SimpleChanges, ViewChild, booleanAttribute, numberAttribute,
} from '@angular/core';
import { DsDotBulletComponent } from '../navigation/ds-dot-bullet.component';

/** Text-only message. Wrap any bold phrases in **double asterisks**. */
export interface DsCarouselMessage {
  id: string;
  text: string;
}

export interface DsMessageCarouselActivation {
  index: number;
  message: DsCarouselMessage;
}

@Component({
  selector: 'ds-message-carousel',
  standalone: true,
  imports: [DsDotBulletComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (messages.length > 0) {
      <section
        class="ds-message-carousel"
        [class.ds-message-carousel--compact]="compact"
        aria-roledescription="carousel"
        [attr.aria-label]="ariaLabel"
        (mouseenter)="setHovered(true)"
        (mouseleave)="setHovered(false)"
        (focusin)="setFocused(true)"
        (focusout)="handleFocusOut($event)"
      >
        <div
          #viewport
          class="ds-message-carousel__viewport"
          [class.ds-message-carousel__viewport--dragging]="dragging"
          [attr.aria-live]="autoPlay && !playbackPaused ? 'off' : 'polite'"
          (pointerdown)="handlePointerDown($event)"
          (pointermove)="handlePointerMove($event)"
          (pointerup)="handlePointerEnd($event)"
          (pointercancel)="handlePointerEnd($event, true)"
          (lostpointercapture)="handlePointerEnd($event, true)"
        >
          @for (message of messages; track $index; let index = $index) {
            <button
              type="button"
              class="ds-message-carousel__message"
              [class.ds-message-carousel__message--active]="index === activeIndex"
              [style.transform]="messageTransform(index)"
              [attr.aria-hidden]="index === activeIndex ? null : 'true'"
              [attr.inert]="index === activeIndex ? null : ''"
              [attr.tabindex]="index === activeIndex ? 0 : -1"
              (click)="activateMessage(index)"
            >
              <span class="ds-message-carousel__message-content">
                @for (part of messageParts(message); track $index) {
                  @if (part.bold) {
                    <strong>{{ part.text }}</strong>
                  } @else {
                    <ng-container>{{ part.text }}</ng-container>
                  }
                }
              </span>
            </button>
          }
        </div>
        @if (messages.length > 1) {
          <div class="ds-message-carousel__indicators" role="group" aria-label="Navegación de mensajes">
            @for (message of messages; track $index; let index = $index) {
              <ds-dot-bullet
                [style]="'filled'"
                [invert]="true"
                [state]="index === activeIndex ? (progressEligible ? 'in-progress' : 'selected') : 'default'"
                [progressDurationSeconds]="remainingSeconds"
                [progressStartPercent]="progressStartPercent"
                [progressPaused]="playbackPaused"
                [ariaCurrent]="index === activeIndex"
                [ariaLabel]="'Ir al mensaje ' + (index + 1) + ' de ' + messages.length"
                [tabIndex]="index === activeIndex ? 0 : -1"
                (pointerdown)="handleIndicatorPointerDown()"
                (pointerup)="handleIndicatorPointerUp()"
                (pointercancel)="clearIndicatorPointerSelection()"
                (clicked)="selectIndicator(index)"
                (keydown)="handleIndicatorKeydown($event, index)"
              />
            }
          </div>
        }
      </section>
    }
  `,
  styles: [`
    :host { display: block; inline-size: 100%; max-inline-size: 100%; min-inline-size: 0; }
    .ds-message-carousel {
      display: flex;
      align-items: center;
      gap: var(--ds-message-carousel-content-gap);
      border-radius: var(--ds-message-carousel-border-radius);
      background: var(--ds-message-carousel-background);
      color: var(--ds-message-carousel-text-color);
      box-shadow: var(--ds-message-carousel-shadow);
      box-sizing: border-box;
    }
    .ds-message-carousel--compact { flex-direction: column; }
    .ds-message-carousel__viewport {
      display: grid;
      flex: 1 1 0;
      align-self: stretch;
      align-items: center;
      min-inline-size: 0;
      overflow: hidden;
      touch-action: pan-y pinch-zoom;
    }
    .ds-message-carousel--compact .ds-message-carousel__viewport { inline-size: 100%; flex-basis: auto; }
    .ds-message-carousel__message {
      grid-area: 1 / 1;
      inline-size: 100%;
      min-inline-size: 0;
      margin: 0;
      padding: var(--ds-message-carousel-padding) 0 var(--ds-message-carousel-padding) var(--ds-message-carousel-padding);
      border: 0;
      border-radius: var(--ds-message-carousel-border-radius) 0 0 var(--ds-message-carousel-border-radius);
      background: transparent;
      color: inherit;
      box-sizing: border-box;
      display: flex;
      align-items: center;
      align-self: stretch;
      font-family: inherit;
      font-size: var(--ds-message-carousel-font-size);
      font-weight: var(--ds-message-carousel-font-weight);
      line-height: var(--ds-message-carousel-line-height);
      text-align: start;
      overflow-wrap: anywhere;
      cursor: pointer;
      pointer-events: none;
      visibility: hidden;
      transition:
        transform var(--ds-message-carousel-transition-duration) cubic-bezier(0.4, 0, 0.2, 1),
        visibility 0s linear var(--ds-message-carousel-transition-duration);
    }
    .ds-message-carousel__message-content { display: block; inline-size: 100%; }
    .ds-message-carousel__message--active { pointer-events: auto; visibility: visible; transition-delay: 0s; }
    .ds-message-carousel__message strong { font-weight: var(--ds-message-carousel-emphasis-font-weight); }
    .ds-message-carousel__message:focus-visible {
      outline: var(--ds-foundation-focus-stroke) solid var(--ds-foundation-focus-color);
      outline-offset: calc(-1 * var(--ds-foundation-focus-stroke));
    }
    .ds-message-carousel__viewport--dragging .ds-message-carousel__message { visibility: visible; transition: none; user-select: none; cursor: grabbing; }
    .ds-message-carousel__indicators {
      display: flex;
      flex: 0 1 auto;
      flex-wrap: wrap;
      align-items: center;
      justify-content: center;
      min-inline-size: 0;
      max-inline-size: 100%;
      gap: var(--ds-message-carousel-indicators-gap);
      padding: var(--ds-message-carousel-padding) var(--ds-message-carousel-padding) var(--ds-message-carousel-padding) 0;
      box-sizing: border-box;
    }
    .ds-message-carousel--compact .ds-message-carousel__message {
      padding: var(--ds-message-carousel-padding) var(--ds-message-carousel-padding) 0;
      border-radius: var(--ds-message-carousel-border-radius) var(--ds-message-carousel-border-radius) 0 0;
      font-size: var(--ds-message-carousel-mobile-font-size);
      line-height: var(--ds-message-carousel-mobile-line-height);
    }
    .ds-message-carousel--compact .ds-message-carousel__indicators {
      inline-size: 100%;
      padding: 0 var(--ds-message-carousel-padding) var(--ds-message-carousel-padding);
    }
    @media (prefers-reduced-motion: reduce) { .ds-message-carousel__message { transition: none; } }
  `],
})
export class DsMessageCarouselComponent implements AfterViewInit, OnChanges, OnDestroy {
  @ViewChild('viewport') private viewportRef?: ElementRef<HTMLElement>;
  @Input() messages: readonly DsCarouselMessage[] = [];
  @Input({ transform: numberAttribute }) selectedIndex = 0;
  @Input({ transform: booleanAttribute }) autoPlay = true;
  @Input({ transform: booleanAttribute }) paused = false;
  @Input() slideDurationSeconds: number | string = 'var(--ds-message-carousel-auto-play-slide-duration-seconds)';
  @Input() ariaLabel = 'Carrusel de mensajes';
  @Output() selectedIndexChange = new EventEmitter<number>();
  @Output() activated = new EventEmitter<DsMessageCarouselActivation>();

  compact = true;
  dragging = false;
  private ready = false;
  private destroyed = false;
  private hovered = false;
  private focused = false;
  private reducedMotion = false;
  private resizeObserver?: ResizeObserver;
  private motionQuery?: MediaQueryList;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private timerStart = 0;
  private elapsed = 0;
  private duration = 0;
  private pointerId: number | null = null;
  private pointerX = 0;
  private pointerY = 0;
  private offset = 0;
  private suppressActivation = false;
  private suppressionTimer: ReturnType<typeof setTimeout> | null = null;
  private indicatorPointerSelection = false;
  private indicatorPointerReleaseTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly motionChanged = (): void => this.zone.run(() => {
    this.reducedMotion = this.motionQuery?.matches ?? false;
    this.restartPlayback();
    this.changeDetector.markForCheck();
  });

  constructor(
    private readonly host: ElementRef<HTMLElement>,
    private readonly changeDetector: ChangeDetectorRef,
    private readonly zone: NgZone,
  ) {}

  get activeIndex(): number {
    return Number.isFinite(this.selectedIndex)
      ? Math.max(0, Math.min(this.messages.length - 1, Math.floor(this.selectedIndex))) : 0;
  }
  get progressEligible(): boolean { return this.autoPlay && !this.reducedMotion && this.duration > 0 && this.messages.length > 1; }
  get playbackPaused(): boolean { return this.paused || this.hovered || this.focused || this.pointerId !== null || (typeof document !== 'undefined' && document.hidden); }
  get remainingSeconds(): number { return Math.max(0, this.duration - this.elapsed) / 1000; }
  get progressStartPercent(): number { return this.duration > 0 ? Math.min(100, this.elapsed / this.duration * 100) : 0; }

  ngAfterViewInit(): void {
    if (typeof window === 'undefined') return;
    this.ready = true;
    this.motionQuery = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    this.motionQuery?.addEventListener('change', this.motionChanged);
    this.reducedMotion = this.motionQuery?.matches ?? false;
    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => this.zone.run(() => this.updateLayout()));
      this.resizeObserver.observe(this.host.nativeElement);
    }
    queueMicrotask(() => {
      if (this.destroyed) return;
      this.updateLayout();
      this.restartPlayback();
      this.changeDetector.markForCheck();
    });
  }
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['messages']) {
      this.resetPointer();
      this.suppressActivation = false;
    }
    if (changes['messages'] || changes['selectedIndex'] || changes['autoPlay'] || changes['slideDurationSeconds']) this.restartPlayback();
    else if (changes['paused']) this.syncPlayback();
  }
  ngOnDestroy(): void {
    this.destroyed = true;
    this.clearTimer();
    if (this.suppressionTimer !== null) clearTimeout(this.suppressionTimer);
    this.clearIndicatorPointerSelection();
    this.resizeObserver?.disconnect();
    this.motionQuery?.removeEventListener('change', this.motionChanged);
    this.resetPointer();
  }

  messageParts(message: DsCarouselMessage): { text: string; bold: boolean }[] {
    const parts: { text: string; bold: boolean }[] = [];
    let cursor = 0;
    while (cursor < message.text.length) {
      const start = message.text.indexOf('**', cursor);
      const end = start < 0 ? -1 : message.text.indexOf('**', start + 2);
      if (end < 0) {
        parts.push({ text: message.text.slice(cursor), bold: false });
        break;
      }
      if (start > cursor) parts.push({ text: message.text.slice(cursor, start), bold: false });
      const bold = end > start + 2;
      parts.push({ text: bold ? message.text.slice(start + 2, end) : '****', bold });
      cursor = end + 2;
    }
    return parts;
  }
  messageTransform(index: number): string { return `translateX(calc(${(index - this.activeIndex) * 100}% + ${this.offset}px))`; }
  activateMessage(index: number): void {
    if (this.suppressActivation || this.dragging || index !== this.activeIndex) return;
    const message = this.messages[index];
    if (message) this.activated.emit({ index, message });
  }
  selectIndex(index: number): void {
    if (this.messages.length === 0 || !Number.isFinite(index)) return;
    const next = Math.max(0, Math.min(this.messages.length - 1, Math.floor(index)));
    if (next === this.activeIndex) return;
    this.selectedIndex = next;
    this.restartPlayback();
    this.selectedIndexChange.emit(next);
    this.changeDetector.markForCheck();
  }
  selectIndicator(index: number): void {
    const selectedWithPointer = this.indicatorPointerSelection;
    this.clearIndicatorPointerSelection();
    if (selectedWithPointer) this.focused = false;
    this.selectIndex(index);
  }
  handleIndicatorPointerDown(): void {
    this.clearIndicatorPointerSelection();
    this.indicatorPointerSelection = true;
  }
  handleIndicatorPointerUp(): void {
    if (!this.indicatorPointerSelection) return;
    this.indicatorPointerReleaseTimer = setTimeout(() => {
      this.indicatorPointerSelection = false;
      this.indicatorPointerReleaseTimer = null;
    }, 0);
  }
  clearIndicatorPointerSelection(): void {
    this.indicatorPointerSelection = false;
    if (this.indicatorPointerReleaseTimer !== null) clearTimeout(this.indicatorPointerReleaseTimer);
    this.indicatorPointerReleaseTimer = null;
  }
  handleIndicatorKeydown(event: KeyboardEvent, index: number): void {
    this.clearIndicatorPointerSelection();
    this.focused = true;
    this.syncPlayback();
    const count = this.messages.length;
    let next: number;
    switch (event.key) {
      case 'ArrowRight': case 'ArrowDown': next = (index + 1) % count; break;
      case 'ArrowLeft': case 'ArrowUp': next = (index - 1 + count) % count; break;
      case 'Home': next = 0; break;
      case 'End': next = count - 1; break;
      default: return;
    }
    event.preventDefault();
    this.selectIndex(next);
    this.host.nativeElement.querySelectorAll<HTMLButtonElement>('.ds-dot-bullet')[next]?.focus();
  }
  setHovered(value: boolean): void { this.hovered = value; this.syncPlayback(); }
  setFocused(value: boolean): void { this.focused = value; this.syncPlayback(); }
  handleFocusOut(event: FocusEvent): void {
    if (event.relatedTarget instanceof Node && this.host.nativeElement.contains(event.relatedTarget)) return;
    this.setFocused(false);
  }
  @HostListener('document:visibilitychange') visibilityChanged(): void { this.syncPlayback(); }
  @HostListener('window:resize') updateLayout(): void {
    if (!this.ready) return;
    const breakpoint = this.readToken('compact-breakpoint');
    this.compact = this.host.nativeElement.getBoundingClientRect().width < breakpoint;
    this.changeDetector.markForCheck();
  }
  handlePointerDown(event: PointerEvent): void {
    if (
      this.messages.length <= 1
      || this.pointerId !== null
      || !event.isPrimary
      || (event.pointerType === 'mouse' && event.button !== 0)
    ) return;
    this.pointerId = event.pointerId;
    this.pointerX = event.clientX;
    this.pointerY = event.clientY;
    try { this.viewportRef?.nativeElement.setPointerCapture(event.pointerId); } catch { /* Pointer capture may be unavailable. */ }
    this.syncPlayback();
  }
  handlePointerMove(event: PointerEvent): void {
    if (event.pointerId !== this.pointerId) return;
    const dx = event.clientX - this.pointerX;
    const dy = event.clientY - this.pointerY;
    if (!this.dragging) {
      if (Math.max(Math.abs(dx), Math.abs(dy)) < this.readToken('drag-activation-distance')) return;
      if (Math.abs(dy) >= Math.abs(dx)) { this.resetPointer(); this.syncPlayback(); return; }
      this.dragging = true;
      this.suppressActivation = true;
    }
    event.preventDefault();
    const width = this.viewportRef?.nativeElement.getBoundingClientRect().width ?? 0;
    this.offset = Math.max(-width, Math.min(width, dx));
  }
  handlePointerEnd(event: PointerEvent, cancelled = false): void {
    if (event.pointerId !== this.pointerId) return;
    const width = this.viewportRef?.nativeElement.getBoundingClientRect().width ?? 0;
    const commit = !cancelled && this.dragging && width > 0 && Math.abs(this.offset) >= width * this.readToken('drag-commit-threshold-scale');
    const direction = this.offset < 0 ? 1 : -1;
    const wasDragging = this.dragging;
    this.resetPointer();
    if (wasDragging) {
      event.preventDefault();
      this.suppressionTimer = setTimeout(() => { this.suppressActivation = false; this.suppressionTimer = null; }, 0);
    }
    if (commit) this.selectIndex((this.activeIndex + direction + this.messages.length) % this.messages.length);
    this.syncPlayback();
  }
  private resetPointer(): void {
    const pointer = this.pointerId;
    this.pointerId = null;
    this.dragging = false;
    this.offset = 0;
    if (pointer !== null) {
      try { this.viewportRef?.nativeElement.releasePointerCapture(pointer); } catch { /* No capture to release. */ }
    }
  }
  private readToken(name: string): number {
    if (!this.ready) return 0;
    const value = Number.parseFloat(window.getComputedStyle(this.host.nativeElement).getPropertyValue(`--ds-message-carousel-${name}`));
    return Number.isFinite(value) ? value : 0;
  }
  private restartPlayback(): void {
    this.clearTimer();
    this.elapsed = 0;
    const seconds = Number(this.slideDurationSeconds);
    this.duration = Math.max(0, Number.isFinite(seconds) ? seconds : this.readToken('auto-play-slide-duration-seconds')) * 1000;
    this.syncPlayback();
  }
  private syncPlayback(): void {
    if (!this.ready) return;
    if (this.timer !== null) {
      this.elapsed = Math.min(this.duration, this.elapsed + Date.now() - this.timerStart);
      this.clearTimer();
    }
    if (!this.progressEligible || this.playbackPaused) return;
    this.timerStart = Date.now();
    this.timer = setTimeout(() => {
      this.timer = null;
      this.selectIndex((this.activeIndex + 1) % this.messages.length);
    }, Math.max(0, this.duration - this.elapsed));
  }
  private clearTimer(): void {
    if (this.timer !== null) clearTimeout(this.timer);
    this.timer = null;
  }
}
